import { ApolloError, gql, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import { convoTypeToIcon } from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import Table from '../../../../common/src/components/Table'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import { Spacer, H4, BodyText, IntroText, EllipsisDiv, BodyTextSpan } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { addFeedback } from '../../../../common/src/feedback'
import {
  FetchTaskAgentsQuery,
  FirmContactsQueryVariables,
  TaskType,
  ConversationType,
  FetchTaskAgentsQueryVariables,
  FirmContactsQuery,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import { Field } from '../components/EditBox'
import EditBoxes from '../components/EditBoxes'
import { PollingQuery } from '../components/PollingQuery'
import { ContactText, getContactText } from '../components/contact'
import { getThreadPathFor } from '../route'

const taskAgentFragment = gql`
  fragment TaskAgent on TaskAgent {
    id
    agent {
      ...Contact
    }
    firmTaskThreadType
    onboardingRoles
    precedence
    workHours
    numTasks
    isCurrentlyWorking
  }
`

export const taskAgentsQuery = gql`
  query FetchTaskAgents($taskId: Int!) {
    task(taskId: $taskId) {
      id
      firmTaskId
      taskAgents {
        ...TaskAgent
      }
      hasLaunched
      taskType
    }
  }
  ${taskAgentFragment}
  ${Fragments.contact}
`

const firmContactsQuery = gql`
  query FirmContacts($firmId: Int!) {
    firm(firmId: $firmId) {
      id
      emailThreadContacts {
        ...Contact
      }
    }
  }
  ${Fragments.contact}
`

const saveTaskAgentMutation = gql`
  mutation SaveTaskAgent(
    $taskId: Int!
    $agentId: Int!
    $precedence: Int
    $workHours: String
    $onboardingRoles: String
  ) {
    saveTaskAgent(
      taskId: $taskId
      agentId: $agentId
      precedence: $precedence
      workHours: $workHours
      onboardingRoles: $onboardingRoles
    ) {
      id
      taskAgents {
        ...TaskAgent
      }
    }
  }
  ${taskAgentFragment}
  ${Fragments.contact}
`

const deleteTaskAgentMutation = gql`
  mutation DeleteTaskAgent($taskId: Int!, $agentId: Int!) {
    deleteTaskAgent(taskId: $taskId, agentId: $agentId) {
      id
      taskAgents {
        ...TaskAgent
      }
    }
  }
  ${taskAgentFragment}
  ${Fragments.contact}
`

const headers = ['Name', 'Settings']
const widths = ['120px', null]
const alignments = ['left', 'left']

interface AgentsSectionProps {
  firmId: number
  taskId: number
  user: FetchUserContextQuery['self']
}

type RowType = FetchTaskAgentsQuery['task']['taskAgents'][0] & { taskId: number }

class AgentsSectionImpl extends React.PureComponent<
  AgentsSectionProps & QueryResult<FetchTaskAgentsQuery, FetchTaskAgentsQueryVariables>
> {
  render() {
    const { firmId, taskId, data, user } = this.props
    if (data == null || data.task == null) {
      return null
    }
    const { taskAgents, firmTaskId, taskType } = data.task
    if (taskAgents == null) {
      return null
    }

    const isSeniorOrAdmin = user && user.permissions.canEditAgents

    // Allow editing when the task is not launched.  Full UI onboarding in APM is not ready yet
    const isEditable = isSeniorOrAdmin

    const taskAgentsByContactId: any = {}
    taskAgents.forEach(a => {
      taskAgentsByContactId[a.agent.id] = a
    })
    const taIds = new Set(taskAgents.map(({ agent }) => agent.id))

    const rows: RowType[] = this.calcSortedTaskAgentRows(taskAgents, taskId)

    const cols = [
      (v: RowType) => {
        const isCurrentlyWorkingOrUnknown = v.isCurrentlyWorking || v.isCurrentlyWorking == null
        return (
          <Link
            color={isCurrentlyWorkingOrUnknown ? 'silver' : 'midway'}
            to={getThreadPathFor({ contactId: v.agent.id, taskId: firmTaskId || 0 })}
          >
            {convoTypeToIcon(v.firmTaskThreadType)}
            <Spacer width={8} />
            <ContactText
              contact={v.agent}
              short
              tooltip={isCurrentlyWorkingOrUnknown ? undefined : `${getContactText(v.agent)} is currently not working`}
            />
          </Link>
        )
      },
      (v: RowType) => {
        const isCurrentlyWorkingOrUnknown = v.isCurrentlyWorking || v.isCurrentlyWorking == null
        return (
          <TaskAgentEditor
            taskId={v.taskId}
            hasPrecedence={v.precedence != null}
            contact={v.agent}
            precedence={v.precedence}
            onboardingRoles={v.onboardingRoles}
            workHours={v.workHours}
            editable={isEditable}
            taskType={taskType}
            firmTaskThreadType={v.firmTaskThreadType}
            isCurrentlyWorkingOrUnknown={isCurrentlyWorkingOrUnknown}
          />
        )
      },
    ]

    return (
      <div>
        <H4>Agents</H4>
        <Spacer height={16} />
        <BodyText>
          Here you define the agents and bosses on this task. Each agent has a number in the "showing order": 1, 2, 3,
          ... etc. If two agents have the same precedence, the order between them will be randomized for each showing.
          Bosses and investors are not offered showings. Investors just get the email reports.
        </BodyText>
        <Spacer height={16} />
        <Table
          rows={rows}
          columns={cols}
          headers={headers}
          widths={widths}
          alignments={alignments}
          spaced
          dataRowComponent={PlainTR}
        />
        {rows.length === 0 && <BodyText>No agents yet...</BodyText>}
        {isEditable && (
          <PollingQuery query={firmContactsQuery} variables={{ firmId }}>
            {(res: QueryResult<FirmContactsQuery, FirmContactsQueryVariables>) => (
              <AgentTypeahead data={res.data} firmId={firmId} taskId={taskId} taIds={taIds} user={user} />
            )}
          </PollingQuery>
        )}
        <Spacer height={32} />
      </div>
    )
  }

  private calcSortedTaskAgentRows(taskAgents: FetchTaskAgentsQuery['task']['taskAgents'], taskId: number): RowType[] {
    return taskAgents
      .map(ta => ({ ...ta, taskId }))
      .sort((a, b) => {
        // always AGENTS before BOSSes
        let ret = (a.firmTaskThreadType || '').localeCompare(b.firmTaskThreadType || '')
        // if both are BOSS, sort alphabetically
        if (ret === 0 && a.firmTaskThreadType === 'BOSS') {
          ret = (a.agent.firstName || '').localeCompare(b.agent.firstName || '')
        }
        // if both are AGENT, sort by precedence
        else if (ret === 0) {
          ret = (a.precedence || 0) - (b.precedence || 0)
        }
        return ret
      })
  }
}

const PlainTR = styled.tr``

interface AgentTypeaheadProps {
  firmId: number
  taskId: number
  taIds: Set<number>
  user: FetchUserContextQuery['self']
  data?: FirmContactsQuery
}

class AgentTypeahead extends React.PureComponent<AgentTypeaheadProps> {
  render() {
    const { data } = this.props

    if (data == null) {
      return null
    }

    return (
      <TypeaheadBox<FirmContactsQuery['firm']['emailThreadContacts'][0]>
        loadFunc={this.loadBound}
        rowFunc={AgentRow}
        onChoose={this.addAgentBound}
        showResultsWhenEmpty
      />
    )
  }

  private loadBound = (prefix: string): Promise<FirmContactsQuery['firm']['emailThreadContacts']> => {
    const { taIds } = this.props
    const pre = (n: any) => n != null && n.toLowerCase().startsWith(prefix)
    const contacts = (this.props.data?.firm && this.props.data?.firm.emailThreadContacts) || []
    const filteredContacts = contacts.filter(
      c =>
        !taIds.has(c.id) &&
        (pre([c.firstName, c.lastName].join(' ')) || pre(c.lastName) || pre(c.addressEmail) || pre(c.addressPhone))
    )

    // doesn't really make much sense to display more than 20 contacts in a page.
    // eg. task 725 has 54 contacts - that's a lot to mount/unmount on each keystroke
    const usefulContacts = filteredContacts.slice(0, Math.min(20, filteredContacts.length))

    return Promise.resolve(usefulContacts)
  }

  private addAgentBound = (agent: FirmContactsQuery['firm']['emailThreadContacts'][0]) => {
    const { taskId } = this.props
    apolloWebClient
      .mutate({
        mutation: saveTaskAgentMutation,
        variables: { taskId, agentId: agent.id },
      })
      .then(() => addFeedback('Added agent'))
      .catch((err: ApolloError) => handleError(err))
  }
}

function AgentRow(props: FirmContactsQuery['firm']['emailThreadContacts'][0]) {
  return (
    <IntroText>
      <EllipsisDiv>
        <ContactText contact={props} />
        <Spacer width={8} />
        <BodyTextSpan>{props.addressEmail}</BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )
}

interface TaskAgentEditorProps {
  taskId: number
  contact: RowType['agent']
  hasPrecedence: boolean
  precedence?: number | null
  workHours?: string | null
  onboardingRoles?: string[] | null
  editable: boolean
  taskType: TaskType
  firmTaskThreadType: ConversationType
  isCurrentlyWorkingOrUnknown: boolean
}

class TaskAgentEditor extends React.PureComponent<TaskAgentEditorProps> {
  render() {
    const { hasPrecedence, contact, editable, taskType, firmTaskThreadType, isCurrentlyWorkingOrUnknown } = this.props

    const isLeasingFirm = taskType === 'LEASING_FIRM'
    const isAgentThread = firmTaskThreadType === 'AGENT'

    const fields: Field<any>[] = [
      !isLeasingFirm && isAgentThread && getPrecedenceField(editable),
      hasPrecedence && getOnsiteHoursField(editable, contact),
      isLeasingFirm && !hasPrecedence && getOnboardingRolesField(editable),
    ].filter(f => f) as Field<any>[]

    return (
      <EditBoxes
        model={this.props}
        fields={fields}
        onEdit={this.saveTaskAgentBound}
        displayType="small"
        onDelete={editable ? this.deleteAgentBound : null}
        color={isCurrentlyWorkingOrUnknown ? undefined : 'midway'}
      />
    )
  }

  private saveTaskAgentBound = (key: string, val: any) => {
    const { precedence, workHours, onboardingRoles } = this.props
    console.log('got onboarding roles')
    console.log(val)
    const obj: any = { precedence, workHours, onboardingRoles }
    obj[key] = val
    return this.saveTaskAgent(obj)
  }

  private deleteAgentBound = () => {
    const { taskId, contact } = this.props
    apolloWebClient
      .mutate({
        mutation: deleteTaskAgentMutation,
        variables: { taskId, agentId: contact.id },
      })
      .then(() => addFeedback('Removed agent'))
      .catch((err: ApolloError) => handleError(err))
  }

  private saveTaskAgent(obj: any) {
    const { taskId, contact } = this.props
    return apolloWebClient
      .mutate({
        mutation: saveTaskAgentMutation,
        variables: Object.assign({ taskId, agentId: contact.id }, obj),
      })
      .then(() => addFeedback('Saved agent'))
      .catch((err: ApolloError) => handleError(err))
  }
}

function getOnboardingRolesField(editable: boolean): Field<string[]> {
  return {
    key: 'onboardingRoles',
    isEditable: editable,
    textFunc: val => (val == null ? 'Onboarding: no role' : 'Role: ' + val.join(', ')),
    editTextFunc: val => (val == null ? '' : val.join(', ')),
    placeholder: 'IT,OPS,MARKETING',
  }
}

function getOnsiteHoursField(editable: boolean, contact: RowType['agent']): Field<string> {
  return {
    key: 'workHours',
    isEditable: editable,
    textFunc: val => (val == null ? (contact.workHours ? 'Default: ' + contact.workHours : 'No work hours') : val),
    editTextFunc: val => val || '',
    colorFunc: val => (val || contact.workHours ? undefined : 'coral'),
    placeholder: 'M W F 9am-5pm Sa-Su 10am-3pm',
  }
}

function getPrecedenceField(editable: boolean): Field<number> {
  return {
    key: 'precedence',
    isEditable: editable,
    textFunc: val => (val == null ? 'non-agent' : '' + val),
    editTextFunc: val => (val == null ? '' : '' + val),
    placeholder: 'non-agent',
    validator: validate
      .integer(n => n > 0)
      .empty('non-agent')
      .empty(''),
  }
}

export default function AgentsSection(props: AgentsSectionProps) {
  const res = useQuery<FetchTaskAgentsQuery, FetchTaskAgentsQueryVariables>(taskAgentsQuery, {
    variables: { taskId: props.taskId },
    fetchPolicy: 'network-only',
  })
  if (res.loading || res.error) {
    return null
  }

  return <AgentsSectionImpl {...props} {...res} />
}
